import { useCallback, useMemo, useState } from "react";
import {
  Alert,
  Anchor,
  Box,
  Button,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import {
  IconAlertCircle,
  IconArrowLeft,
  IconArrowRight,
  IconCircleCheck,
} from "@tabler/icons-react";
import { Link, useSearchParams } from "react-router-dom";
import { portalPasswordService } from "../../services/portalPassword.service";
import { usePortalAuthStore } from "../../store/portalAuth.store";
import {
  PORTAL_PASSWORD_REQUIREMENTS,
  validatePortalPassword,
  validatePortalPasswordConfirmation,
} from "../../utils/portalPasswordValidation";

interface ResetFormValues {
  code: string;
  newPassword: string;
  confirmPassword: string;
}

export function PortalPasswordResetPage() {
  const [searchParams] = useSearchParams();
  const caseId = searchParams.get("caseId");
  const logout = usePortalAuthStore((state) => state.logout);
  const portalEmail = usePortalAuthStore((state) => state.email);
  const [step, setStep] = useState<"request" | "reset" | "complete">("request");
  const [resetEmail, setResetEmail] = useState("");
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const loginUrl = useMemo(
    () =>
      caseId
        ? `/portal/login?caseId=${encodeURIComponent(caseId)}`
        : "/portal/login",
    [caseId]
  );
  const isAccountRecovery = useMemo(
    () => searchParams.get("from") === "account" && Boolean(portalEmail),
    [portalEmail, searchParams]
  );
  const backUrl = useMemo(
    () => (isAccountRecovery ? "/portal/account" : loginUrl),
    [isAccountRecovery, loginUrl]
  );
  const recoveryEmail = useMemo(
    () => (isAccountRecovery && portalEmail ? portalEmail : ""),
    [isAccountRecovery, portalEmail]
  );
  const validateConfirmedPassword = useCallback(
    (value: string, values: ResetFormValues) =>
      validatePortalPasswordConfirmation(value, values.newPassword),
    []
  );
  const requestForm = useForm<{ email: string }>({
    mode: "uncontrolled",
    initialValues: { email: recoveryEmail },
    validateInputOnBlur: true,
    validate: {
      email: (value) =>
        /^\S+@\S+\.\S+$/.test(value.trim())
          ? null
          : "Enter a valid email address",
    },
  });
  const resetForm = useForm<ResetFormValues>({
    mode: "uncontrolled",
    initialValues: {
      code: "",
      newPassword: "",
      confirmPassword: "",
    },
    validateInputOnBlur: true,
    validate: {
      code: (value) =>
        /^\d{6}$/.test(value) ? null : "Enter the 6-digit code",
      newPassword: validatePortalPassword,
      confirmPassword: validateConfirmedPassword,
    },
  });

  const requestCode = useCallback(async (email: string) => {
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      await portalPasswordService.requestReset(email);
      setResetEmail(email);
      setStep("reset");
    } catch {
      setSubmitError("We couldn't send a code right now. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  const handleRequest = useCallback(
    async (values: { email: string }) => requestCode(values.email.trim()),
    [requestCode]
  );

  const handleResend = useCallback(async () => {
    await requestCode(resetEmail);
  }, [requestCode, resetEmail]);

  const handleChangeEmail = useCallback(() => {
    setSubmitError(null);
    setStep("request");
  }, []);

  const handleReset = useCallback(
    async (values: ResetFormValues) => {
      setIsSubmitting(true);
      setSubmitError(null);
      try {
        await portalPasswordService.reset(
          resetEmail,
          values.code,
          values.newPassword
        );
        logout();
        setStep("complete");
      } catch (error) {
        setSubmitError(
          error instanceof Error
            ? error.message
            : "We couldn't reset your password. Please try again."
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [logout, resetEmail]
  );

  return (
    <Box maw={460} mx="auto" px="md" py="xl">
      <Stack gap="lg">
        <Anchor component={Link} to={backUrl} size="sm">
          <IconArrowLeft size={14} />{" "}
          {isAccountRecovery ? "Back to Account Settings" : "Back to sign in"}
        </Anchor>
        <Stack gap={4}>
          <Title order={1}>Reset your password</Title>
          <Text c="dimmed">
            We&apos;ll verify your email with a one-time code before changing
            your password.
          </Text>
        </Stack>

        {step === "complete" ? (
          <Alert
            icon={<IconCircleCheck size={18} />}
            color="green"
            title="Password updated"
          >
            Your previous portal sessions were signed out. You can now{" "}
            <Anchor component={Link} to={loginUrl}>
              sign in with your new password
            </Anchor>
            .
          </Alert>
        ) : step === "request" ? (
          <form onSubmit={requestForm.onSubmit(handleRequest)}>
            <Stack gap="md">
              {isAccountRecovery ? (
                <Text size="sm">
                  Send a verification code to <strong>{recoveryEmail}</strong>.
                </Text>
              ) : (
                <TextInput
                  withAsterisk
                  label="Email"
                  type="email"
                  autoComplete="username"
                  key={requestForm.key("email")}
                  {...requestForm.getInputProps("email")}
                />
              )}
              {submitError && (
                <Alert icon={<IconAlertCircle size={16} />} color="red">
                  {submitError}
                </Alert>
              )}
              <Button
                type="submit"
                loading={isSubmitting}
                rightSection={<IconArrowRight size={17} />}
              >
                Send Verification Code
              </Button>
            </Stack>
          </form>
        ) : (
          <form onSubmit={resetForm.onSubmit(handleReset)}>
            <Stack gap="md">
              <Text size="sm" c="dimmed">
                If {resetEmail} has an active portal account, a code was sent.
              </Text>
              <TextInput
                withAsterisk
                label="Verification code"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                key={resetForm.key("code")}
                {...resetForm.getInputProps("code")}
              />
              <PasswordInput
                withAsterisk
                label="New password"
                autoComplete="new-password"
                description={PORTAL_PASSWORD_REQUIREMENTS}
                key={resetForm.key("newPassword")}
                {...resetForm.getInputProps("newPassword")}
              />
              <PasswordInput
                withAsterisk
                label="Confirm new password"
                autoComplete="new-password"
                key={resetForm.key("confirmPassword")}
                {...resetForm.getInputProps("confirmPassword")}
              />
              {submitError && (
                <Alert icon={<IconAlertCircle size={16} />} color="red">
                  {submitError}
                </Alert>
              )}
              <Button type="submit" loading={isSubmitting}>
                Reset Password
              </Button>
              <Button
                type="button"
                variant="subtle"
                loading={isSubmitting}
                onClick={handleResend}
              >
                Resend Code
              </Button>
              {!isAccountRecovery && (
                <Button
                  type="button"
                  variant="subtle"
                  onClick={handleChangeEmail}
                >
                  Use a Different Email
                </Button>
              )}
            </Stack>
          </form>
        )}
      </Stack>
    </Box>
  );
}
